import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Paper, Box, Typography, useTheme, useMediaQuery, Toolbar, IconButton,
  TextField, Button, ButtonGroup, FormControlLabel, Checkbox, Alert,
  InputAdornment, LinearProgress, Divider, Card, CardActionArea, Chip,
  Stepper, Step, StepLabel, StepConnector, Switch, FormGroup,
  styled, stepConnectorClasses, CircularProgress,
} from "@mui/material";
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import type { StepIconProps } from '@mui/material/StepIcon';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from "../../state/store";
import { setMode } from '../../state/global/globalSlice';
import {
  loginUserAsync, registerUserAsync, clearRegistration,
  fetchCompanyPreviewAsync, CompanyPreview, ModuleLicenseInfo, fetchBusinessPlansAsync,
  fetchSignupPlansAsync,
  fetchCountriesAsync, registerUserToCompanyAsync, createCheckoutSessionAsync,
  checkEmailExistsAsync, restoreReduxSession,
} from '../../state/authentication/authenticationSlice';
import type { SSOProviderPublic } from '../../state/clientConfig/clientConfigSlice';
import { Logger } from "../../utils/Logger";
import { LocalStorageAuth } from '../../utils/LocalStorageAuth';
import { trackLogin } from '../../utils/Infralytiqs';
import { CustomPaletteColor } from '../../theme';
import { Visibility, VisibilityOff, Check } from '@mui/icons-material';
import { EMAIL_PATTERN, THEME_DARK, API_BASE_URL } from '../../envConfig';
import { COUNTRY_TO_CURRENCY } from '../../modules/ManagePlan/Plan/types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LoadingButton from '@mui/lab/LoadingButton';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { SiteLogo } from '../../components/SiteLogo';
import { ThemeModeToggle } from '../../components/ThemeModeToggle/ThemeModeToggle';
import { ConnectedDotsBackground } from '../../components/ConnectedDotsBackground';
import { sanitizeLoginSignInTermsHtml } from '../../utils/sanitizeLoginSignInTermsHtml';

type AuthMode = 'signin' | 'signup';

interface CountryOption {
  code: string;
  label: string;
  dialCode: string;
}

const filterSignupCountryOptions = createFilterOptions<CountryOption>({
  stringify: (option) => `${option.label} ${option.code} ${option.dialCode}`,
});

const MOBILE_PATTERN = /^\+\d{1,4}[\s-]?\d{4,14}$/;

/** Used when domain config does not set `loginSignInTermsNotice`. */
const DEFAULT_LOGIN_SIGN_IN_TERMS_NOTICE =
  'By clicking the Sign In button, you automatically agree to the terms and conditions of Sharad Tech Pvt. Ltd.';

/** Mobile Safari/Chrome: discourage sentence-case keyboard from defaulting the first character to uppercase. */
const EMAIL_FIELD_HTML_INPUT = {
  autoCapitalize: 'none' as const,
  autoCorrect: 'off' as const,
  spellCheck: false,
  autoComplete: 'email',
};

const isAtLeast18 = (dobStr: string): boolean => {
  if (!dobStr) return false;
  const dob = new Date(dobStr);
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }
  return age >= 18;
};

interface ServerPlanHighlight {
  module: string;
  features: string[];
}

interface CurrencyPricing {
  monthlyPrice: number;
  annualMonthlyPrice: number;
}

interface SupportedCurrency {
  code: string;
  symbol: string;
  label: string;
  zeroDecimal?: boolean;
}

interface ServerBusinessPlan {
  _id?: string;
  key: string;
  label: string;
  tagline: string;
  noOfUsers: number;
  modules: string[];
  highlights: ServerPlanHighlight[];
  monthlyPriceInPence?: number;
  annualMonthlyPriceInPence?: number;
  pricing?: Record<string, CurrencyPricing>;
  upgradeRoutes?: { monthly: any[]; annual: any[] };
  downgradeRoutes?: { monthly: any[]; annual: any[] };
}

const WIZARD_STEPS = ['Company Setup', 'Credentials', 'Personal Details', 'Business Plan', 'Review & Create'];

const WizardConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: { top: 14 },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: { backgroundColor: theme.palette.secondary.main },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: { backgroundColor: theme.palette.secondary.main },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300],
    borderRadius: 1,
  },
}));

function WizardStepIcon(props: StepIconProps) {
  const { active, completed, icon } = props;
  const theme = useTheme();

  return (
    <Box sx={{
      width: 30, height: 30, borderRadius: '50%',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: (active || completed) ? theme.palette.secondary.main : (theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]),
      color: (active || completed) ? theme.palette.secondary.contrastText : theme.palette.text.secondary,
      fontWeight: 700, fontSize: '0.8rem',
      transition: 'all 0.3s ease',
      boxShadow: active ? `0 0 0 3px ${theme.palette.secondary.main}33` : 'none',
    }}>
      {completed ? <Check sx={{ fontSize: 18 }} /> : icon}
    </Box>
  );
}

export const Login = () => {
  const isNonMobile = useMediaQuery('(min-width: 600px)');
  const theme = useTheme();
  const loginThemeLogoKey = theme.palette.mode === 'dark' ? 'dark' : 'light';
  const hasDomainThemeLogoOnLogin = useSelector(
    (s: RootState) => Boolean(s.clientConfig.data?.themeBrandLogos?.[loginThemeLogoKey]),
  );
  const userSignUpEnabled = useSelector(
    (s: RootState) => s.clientConfig.data?.userSignUpEnabled !== false,
  );
  const dispatch = useDispatch<AppDispatch>();
  const themePreference = useSelector((s: RootState) => s.global.mode);
  const navigation = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [authMode, setAuthMode] = useState<AuthMode>('signin');
  const [formError, setFormError] = useState<string>('');
  const effectiveAuthMode = useMemo(
    (): AuthMode => (userSignUpEnabled ? authMode : 'signin'),
    [userSignUpEnabled, authMode],
  );

  // Sign In fields
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');

  // Wizard step (used when no companyId)
  const [activeStep, setActiveStep] = useState(0);

  // Sign Up fields – Company (Step 1)
  const [signUpCompanyName, setSignUpCompanyName] = useState('');
  const [companyAddr1, setCompanyAddr1] = useState('');
  const [companyAddr2, setCompanyAddr2] = useState('');
  const [companyCity, setCompanyCity] = useState('');
  const [companyCountry, setCompanyCountry] = useState('');
  const [companyZip, setCompanyZip] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');

  // Sign Up fields – Credentials (Step 2)
  const [signUpEmail, setSignUpEmail] = useState('');
  const [signUpPassword, setSignUpPassword] = useState('');
  const [signUpConfirmPassword, setSignUpConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Sign Up fields – Personal (Step 3)
  const [signUpFirstName, setSignUpFirstName] = useState('');
  const [signUpLastName, setSignUpLastName] = useState('');
  const [signUpMobile, setSignUpMobile] = useState('');
  const [signUpDob, setSignUpDob] = useState('');
  const [signUpTerms, setSignUpTerms] = useState(false);
  const [signUpPrivacy, setSignUpPrivacy] = useState(false);

  // Sign Up fields – Plan (Step 4)
  const [signUpPlan, setSignUpPlan] = useState<string>('starter');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [selectedCurrency, setSelectedCurrency] = useState<string>('GBP');
  const [supportedCurrencies, setSupportedCurrencies] = useState<SupportedCurrency[]>([]);
  const [availablePlans, setAvailablePlans] = useState<ServerBusinessPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansSource, setPlansSource] = useState<'db' | 'env'>('env');
  const signupPlansCountryRef = useRef<string>('');

  // Countries fetched from server (GET /auth/countries → COUNTRIES_LIST)
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [countriesLoading, setCountriesLoading] = useState(false);

  // Field-level validation errors
  const [emailError, setEmailError] = useState<string | null>(null);
  const [mobileError, setMobileError] = useState<string | null>(null);
  const [dobError, setDobError] = useState<string | null>(null);

  // Company preview state (join-company flow)
  const [companyPreviewLoading, setCompanyPreviewLoading] = useState(false);
  const [companyPreviewError, setCompanyPreviewError] = useState<string | null>(null);
  const [companyPreview, setCompanyPreview] = useState<CompanyPreview | null>(null);
  const [joinCompanyId, setJoinCompanyId] = useState<string | null>(null);
  const [joinModules, setJoinModules] = useState<string[]>([]);
  const [moduleLicenseError, setModuleLicenseError] = useState<string | null>(null);
  const [paymentCancelled, setPaymentCancelled] = useState(false);
  const [existingUserDetected, setExistingUserDetected] = useState(false);
  const [emailCheckLoading, setEmailCheckLoading] = useState(false);

  // SSO state
  const [ssoLoading, setSsoLoading] = useState(false);
  const [ssoError, setSsoError] = useState<string | null>(null);

  const allSsoProviders: SSOProviderPublic[] = useSelector(
    (state: RootState) => state.clientConfig.data?.ssoProviders || []
  );
  const customLoginSignInTermsRaw = useSelector(
    (state: RootState) => state.clientConfig.data?.loginSignInTermsNotice?.trim() ?? '',
  );
  const loginSignInTermsHtml = useMemo(() => {
    if (!customLoginSignInTermsRaw) return null;
    const safe = sanitizeLoginSignInTermsHtml(customLoginSignInTermsRaw).trim();
    return safe.length > 0 ? safe : null;
  }, [customLoginSignInTermsRaw]);
  const currentHashPath = `/#${window.location.hash.replace('#', '').split('?')[0] || '/login'}`;
  const ssoProviders = useMemo(
    () => allSsoProviders.filter(p => p.loginPageHashPath === currentHashPath),
    [allSsoProviders, currentHashPath]
  );

  const { loading, error, user, registering, registerError, registerSuccess } = useSelector(
    (state: RootState) => state.authentication
  );

  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const mode = searchParams.get('mode');
    if (mode === 'signup') setAuthMode('signup');

    const qEmail = searchParams.get('email');
    const qFirstName = searchParams.get('firstName');
    const qLastName = searchParams.get('lastName');
    const qMobile = searchParams.get('mobile');
    const qCountry = searchParams.get('country');
    const qCompanyName = searchParams.get('companyName');
    const qDob = searchParams.get('dob');
    const qPlan = searchParams.get('plan');

    if (qEmail) setSignUpEmail(qEmail);
    if (qFirstName) setSignUpFirstName(qFirstName);
    if (qLastName) setSignUpLastName(qLastName);
    if (qMobile) setSignUpMobile(qMobile);
    if (qCountry) setCompanyCountry(qCountry);
    if (qCompanyName) setSignUpCompanyName(qCompanyName);
    if (qDob) setSignUpDob(qDob);

    setCountriesLoading(true);
    dispatch(fetchCountriesAsync())
      .unwrap()
      .then((result: any) => setCountries(result.data || []))
      .catch(() => {})
      .finally(() => setCountriesLoading(false));

    setPlansLoading(true);
    const hostname = window.location.hostname;
    const hostParts = hostname.split('.');
    const detectedRootDomain = hostParts.length >= 2 ? hostParts.slice(-2).join('.') : hostname;
    const initialCountry = qCountry || '';

    const loadPlans = initialCountry
      ? dispatch(fetchSignupPlansAsync({ country: initialCountry, rootDomain: detectedRootDomain }))
      : dispatch(fetchBusinessPlansAsync());

    loadPlans
      .unwrap()
      .then((result: any) => {
        const responseData = result.data || {};
        const plans: ServerBusinessPlan[] = responseData.plans || responseData || [];
        const currencies: SupportedCurrency[] = responseData.supportedCurrencies || [];
        setAvailablePlans(Array.isArray(plans) ? plans : []);
        if (currencies.length > 0) setSupportedCurrencies(currencies);
        setPlansSource(responseData.source || 'env');
        if (initialCountry) signupPlansCountryRef.current = initialCountry;
        if (responseData.currency) {
          setSelectedCurrency(responseData.currency);
        }
        if (qPlan && plans.some((p: any) => p.key === qPlan)) {
          setSignUpPlan(qPlan);
        } else if (plans.length > 0) {
          const mostExpensive = [...plans].sort((a, b) =>
            (b.monthlyPriceInPence || 0) - (a.monthlyPriceInPence || 0)
          )[0];
          setSignUpPlan(mostExpensive.key);
        }
        setPlansLoading(false);
      })
      .catch(() => setPlansLoading(false));

    // SSO callback handling
    const qSsoToken = searchParams.get('ssoToken');
    const qSsoError = searchParams.get('ssoError');
    const qSsoEmail = searchParams.get('ssoEmail');

    if (qSsoToken) {
      setSsoLoading(true);
      LocalStorageAuth.setToken(qSsoToken);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('ssoToken');
      setSearchParams(newParams, { replace: true });
      dispatch(restoreReduxSession())
        .unwrap()
        .then(() => setSsoLoading(false))
        .catch(() => {
          setSsoLoading(false);
          setSsoError('SSO session restore failed. Please try again.');
        });
    }

    if (qSsoError) {
      const errorMessages: Record<string, string> = {
        no_account: `The logged in user${qSsoEmail ? ` (${qSsoEmail})` : ''} is not registered with our portal. Please register using the Sign Up form.`,
        account_inactive: 'Your account is inactive. Please contact your administrator.',
        account_pending: 'Your account is pending activation. Please check your email for the activation link.',
        oauth_denied: 'SSO authentication was cancelled or denied.',
        provider_not_configured: 'This SSO provider is not configured. Please contact the administrator.',
        token_error: 'Authentication failed. Please try again.',
        no_email: 'Could not retrieve your email from the identity provider. Please try again.',
        server_error: 'An unexpected error occurred. Please try again.',
      };
      setSsoError(errorMessages[qSsoError] || 'SSO authentication failed. Please try again.');
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('ssoError');
      newParams.delete('ssoEmail');
      setSearchParams(newParams, { replace: true });
    }

    const qPaymentCancelled = searchParams.get('paymentCancelled');
    if (qPaymentCancelled === 'true') {
      setPaymentCancelled(true);
      setSignUpPlan('starter');
      setAuthMode('signup');
    }

    const qCompanyId = searchParams.get('companyId');
    const qModuleInfo = searchParams.get('moduleInfo');
    let decodedModules: string[] = [];
    if (qModuleInfo) {
      try {
        decodedModules = atob(qModuleInfo).split(',').map(m => m.trim()).filter(Boolean);
      } catch (_) {
        Logger.debug("Login.tsx", "Failed to decode moduleInfo query param");
      }
    }

    if (qCompanyId) {
      setJoinCompanyId(qCompanyId);
      if (decodedModules.length > 0) setJoinModules(decodedModules);
      setCompanyPreviewLoading(true);
      setCompanyPreviewError(null);
      dispatch(fetchCompanyPreviewAsync(qCompanyId))
        .unwrap()
        .then((result: any) => {
          const preview: CompanyPreview = result.data;
          if (preview) {
            setCompanyPreview(preview);
            if (preview.name && preview.name !== 'NA') setSignUpCompanyName(preview.name);
            if (preview.country && !qCountry) setCompanyCountry(preview.country);
            const modulesToCheck = decodedModules.length > 0 ? decodedModules : (preview.availableModules || []);
            if (decodedModules.length === 0 && modulesToCheck.length > 0) setJoinModules(modulesToCheck);
            checkModulesLicense(modulesToCheck, preview);
          }
          setCompanyPreviewLoading(false);
        })
        .catch((err: any) => {
          Logger.debug("Login.tsx", `Company preview fetch failed: ${err}`);
          setCompanyPreviewError('Could not load company details for the provided ID.');
          setCompanyPreviewLoading(false);
        });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user && !loading) {
      setTimeout(() => navigation('/dashboard'), 100);
    }
  }, [user, loading, navigation]);

  useEffect(() => {
    if (registerSuccess) setFormError('');
  }, [registerSuccess]);

  useEffect(() => {
    if (companyCountry) {
      const code = companyCountry.toUpperCase();

      if (code !== signupPlansCountryRef.current) {
        const hostname = window.location.hostname;
        const hostParts = hostname.split('.');
        const detectedRootDomain = hostParts.length >= 2 ? hostParts.slice(-2).join('.') : hostname;

        setPlansLoading(true);
        dispatch(fetchSignupPlansAsync({ country: code, rootDomain: detectedRootDomain }))
          .unwrap()
          .then((result: any) => {
            const responseData = result.data || {};
            const plans: ServerBusinessPlan[] = responseData.plans || responseData || [];
            const currencies: SupportedCurrency[] = responseData.supportedCurrencies || [];
            setAvailablePlans(Array.isArray(plans) ? plans : []);
            if (currencies.length > 0) setSupportedCurrencies(currencies);
            setPlansSource(responseData.source || 'env');
            signupPlansCountryRef.current = code;

            if (responseData.currency) {
              setSelectedCurrency(responseData.currency);
            } else {
              setSelectedCurrency(COUNTRY_TO_CURRENCY[code] || 'GBP');
            }

            if (plans.length > 0) {
              const mostExpensive = [...plans].sort((a, b) =>
                (b.monthlyPriceInPence || 0) - (a.monthlyPriceInPence || 0)
              )[0];
              setSignUpPlan(mostExpensive.key);
            }
            setPlansLoading(false);
          })
          .catch(() => {
            setSelectedCurrency(COUNTRY_TO_CURRENCY[code] || 'GBP');
            setPlansLoading(false);
          });
      }
    }
  }, [companyCountry, dispatch]);

  const signupCountryOptions = useMemo(() => {
    const c = companyCountry.trim();
    if (!c) return countries;
    const up = c.toUpperCase();
    if (!countries.some((x) => x.code.toUpperCase() === up)) {
      return [{ code: up, label: c, dialCode: '' }, ...countries];
    }
    return countries;
  }, [countries, companyCountry]);

  const selectedSignupCountry = useMemo(() => {
    if (!companyCountry.trim()) return null;
    const up = companyCountry.toUpperCase();
    return signupCountryOptions.find((x) => x.code.toUpperCase() === up) ?? null;
  }, [signupCountryOptions, companyCountry]);

  const switchMode = useCallback((mode: AuthMode) => {
    if (mode === 'signup' && !userSignUpEnabled) return;
    setAuthMode(mode);
    setFormError('');
    setActiveStep(0);
    dispatch(clearRegistration());
    const newParams = new URLSearchParams(searchParams);
    if (mode === 'signup') newParams.set('mode', 'signup');
    else newParams.delete('mode');
    setSearchParams(newParams, { replace: true });
  }, [dispatch, searchParams, setSearchParams, userSignUpEnabled]);

  useEffect(() => {
    if (userSignUpEnabled || authMode !== 'signup') return;
    switchMode('signin');
  }, [userSignUpEnabled, authMode, switchMode]);

  const checkModulesLicense = useCallback((modules: string[], preview: CompanyPreview | null) => {
    if (!preview?.moduleLicenseInfo || modules.length === 0) {
      setModuleLicenseError(null);
      return;
    }
    const issues: string[] = [];
    for (const mod of modules) {
      const info = preview.moduleLicenseInfo.find((m: ModuleLicenseInfo) => m.module === mod);
      if (!info) issues.push(`"${mod}" is not available in this company's plan`);
      else if (info.remainingSeats <= 0) issues.push(`"${mod}" has no remaining seats (${info.registeredUsers}/${info.maxUsers} used)`);
    }
    setModuleLicenseError(issues.length > 0 ? issues.join('. ') + '.' : null);
  }, []);

  const validateEmail = useCallback((value: string) => {
    if (!value) { setEmailError(null); return; }
    setEmailError(EMAIL_PATTERN.test(value) ? null : 'Please enter a valid email address.');
  }, []);

  const checkEmailForWizard = useCallback(async (value: string) => {
    if (!value || !EMAIL_PATTERN.test(value) || joinCompanyId) return;
    setEmailCheckLoading(true);
    try {
      const result = await dispatch(checkEmailExistsAsync(value)).unwrap();
      const exists = result?.data?.exists === true;
      setExistingUserDetected(exists);
      if (exists) {
        setSignUpPassword('');
        setSignUpConfirmPassword('');
      }
    } catch {
      setExistingUserDetected(false);
    } finally {
      setEmailCheckLoading(false);
    }
  }, [dispatch, joinCompanyId]);

  const validateMobile = useCallback((value: string) => {
    if (!value) { setMobileError(null); return; }
    setMobileError(MOBILE_PATTERN.test(value) ? null : 'Mobile must start with a country code (e.g. +44, +91).');
  }, []);

  const validateDob = useCallback((value: string) => {
    if (!value) { setDobError(null); return; }
    setDobError(isAtLeast18(value) ? null : 'You must be at least 18 years old to register.');
  }, []);

  // ─── Step validation ───
  const isStep1Valid = (): boolean =>
    !!signUpCompanyName.trim() && !!companyAddr1.trim() && !!companyCity.trim() && !!companyCountry && !!companyZip.trim();

  const isStep2Valid = (): boolean => {
    if (!signUpEmail || !EMAIL_PATTERN.test(signUpEmail)) return false;
    if (!existingUserDetected) {
      if (signUpPassword.length < 6) return false;
      if (signUpPassword !== signUpConfirmPassword) return false;
    }
    return true;
  };

  const isStep3Valid = (): boolean => {
    if (existingUserDetected) return true;
    if (!signUpFirstName.trim() || !signUpLastName.trim()) return false;
    if (!signUpMobile || !MOBILE_PATTERN.test(signUpMobile)) return false;
    if (!signUpDob || !isAtLeast18(signUpDob)) return false;
    if (!signUpTerms || !signUpPrivacy) return false;
    return true;
  };

  const isJoinFormValid = (): boolean => {
    if (!signUpFirstName.trim() || !signUpLastName.trim()) return false;
    if (!signUpEmail || !EMAIL_PATTERN.test(signUpEmail)) return false;
    if (signUpPassword.length < 6) return false;
    if (signUpPassword !== signUpConfirmPassword) return false;
    if (!signUpMobile || !MOBILE_PATTERN.test(signUpMobile)) return false;
    if (!signUpDob || !isAtLeast18(signUpDob)) return false;
    if (!signUpTerms || !signUpPrivacy) return false;
    return true;
  };

  const getPlanPriceInCurrency = (plan: ServerBusinessPlan, cycle: 'monthly' | 'annual', currCode: string): number => {
    const cp = plan.pricing?.[currCode];
    if (cp) return cycle === 'annual' ? cp.annualMonthlyPrice : cp.monthlyPrice;
    return cycle === 'annual' ? (plan.annualMonthlyPriceInPence || 0) : (plan.monthlyPriceInPence || 0);
  };

  const getCurrencySymbol = (code: string): string => {
    const c = supportedCurrencies.find(sc => sc.code === code);
    return c?.symbol || code;
  };

  const getSelectedPlanPrice = (): number => {
    const plan = availablePlans.find(p => p.key === signUpPlan);
    if (!plan) return 0;
    return getPlanPriceInCurrency(plan, billingCycle, selectedCurrency);
  };

  // ─── Handlers ───
  const handleLoginEvent = (e: React.FormEvent) => {
    e.preventDefault();
    if (!EMAIL_PATTERN.test(email)) { setFormError('Please enter a valid email address.'); return; }
    setFormError('');
    const emailAtLogin = email;
    dispatch(loginUserAsync({ email, password })).unwrap().then((result: any) => {
      if (result.data) {
        trackLogin(result.data?.email || emailAtLogin, {
          userId: result.data?._id,
          method: 'password',
          companyId: result.data?.companyId,
        });
        setEmail(''); setPassword('');
        setTimeout(() => { if (result.data?._id) navigation('/dashboard'); }, 500);
      }
    }).catch((err: any) => setFormError(err.message || 'Login failed. Please try again.'));
  };

  const handleSSOLogin = (providerId: string) => {
    const redirectUri = encodeURIComponent(window.location.origin);
    window.location.href = `${API_BASE_URL}/auth/sso/${providerId}/initiate?redirect_uri=${redirectUri}`;
  };

  const handleNext = () => {
    setFormError('');
    setActiveStep(prev => {
      if (prev === 1 && existingUserDetected) return 3;
      return prev + 1;
    });
  };
  const handleBack = () => {
    setFormError('');
    setActiveStep(prev => {
      if (prev === 3 && existingUserDetected) return 1;
      return prev - 1;
    });
  };

  const buildProfile = () => ({
    firstName: signUpFirstName,
    lastName: signUpLastName,
    country: companyCountry,
    termsAndConditions: signUpTerms,
    privacyPolicy: signUpPrivacy,
    emailNotifications: true,
    smsNotifications: false,
    whatsAppNotifications: true,
    dob: signUpDob,
  });

  const buildCompanyFields = () => ({
    companyName: signUpCompanyName.trim() || undefined,
    companyAddressLine1: companyAddr1.trim() || undefined,
    companyAddressLine2: companyAddr2.trim() || undefined,
    companyCity: companyCity.trim() || undefined,
    companyCountry: companyCountry || undefined,
    companyZip: companyZip.trim() || undefined,
    companyWebsite: companyWebsite.trim() || undefined,
    companyPhone: companyPhone.trim() || undefined,
  });

  const handleCreateAccount = () => {
    setFormError('');
    const companyFields = buildCompanyFields();
    const priceInPence = getSelectedPlanPrice();
    const profile = existingUserDetected ? undefined : buildProfile();
    const mobile = existingUserDetected ? undefined : signUpMobile;
    const pw = existingUserDetected ? undefined : signUpPassword;

    const selectedPlanObj = availablePlans.find(p => p.key === signUpPlan);
    const planPayload: Record<string, any> = { businessPlan: signUpPlan };
    if (plansSource === 'db' && selectedPlanObj?._id) {
      planPayload.businessPlanId = selectedPlanObj._id;
    }

    if (priceInPence > 0) {
      dispatch(createCheckoutSessionAsync({
        email: signUpEmail,
        password: pw as any,
        mobile: mobile as any,
        ...planPayload,
        billingCycle,
        currency: selectedCurrency,
        ...companyFields,
        profile: profile as any,
      }))
        .unwrap()
        .then((result: any) => {
          if (result.data?.checkoutUrl) window.location.href = result.data.checkoutUrl;
        })
        .catch((err: any) => setFormError(err || 'Failed to initiate payment. Please try again.'));
      return;
    }

    dispatch(registerUserAsync({
      email: signUpEmail,
      password: pw as any,
      mobile: mobile as any,
      ...planPayload,
      ...companyFields,
      profile: profile as any,
    }));
  };

  const handleJoinCompany = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    if (joinModules.length === 0) { setFormError('No modules specified. The registration link may be invalid.'); return; }
    if (moduleLicenseError) { setFormError(moduleLicenseError); return; }
    dispatch(registerUserToCompanyAsync({
      email: signUpEmail,
      password: signUpPassword,
      mobile: signUpMobile,
      companyId: joinCompanyId!,
      moduleAndRole: joinModules.map(mod => ({ module: mod, role: 'user' })),
      profile: buildProfile(),
    }));
  };

  // ─── Render helpers ───

  const renderSignInForm = () => (
    <Box component="form" noValidate autoComplete="off" width="100%" justifyContent="center" onSubmit={handleLoginEvent} p="1rem">
      {ssoError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSsoError(null)}>{ssoError}</Alert>}
      {ssoLoading && <LinearProgress color="secondary" sx={{ mb: 1 }} />}

      <TextField margin="normal" label="Email" variant="outlined" placeholder="Enter email" color="secondary" fullWidth required type="email"
        error={!!formError && effectiveAuthMode === 'signin'} helperText={effectiveAuthMode === 'signin' ? formError : ''} value={email} onChange={(e) => setEmail(e.target.value)}
        slotProps={{ htmlInput: EMAIL_FIELD_HTML_INPUT }} />
      <TextField margin="normal" label="Password" variant="outlined" placeholder="Enter password" color="secondary" fullWidth required type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
      <LoadingButton sx={{ mt: '1rem' }} color="secondary" loading={loading} loadingPosition="start" startIcon={<LoginIcon />} fullWidth variant="contained" type="submit" disabled={loading || ssoLoading}>
        {loading ? 'Logging in...' : 'Sign In'}
      </LoadingButton>
      {error && <Typography variant="body2" color="error" width="100%" mt={2} textAlign="center">{error}</Typography>}

      {ssoProviders.length > 0 && (
        <>
          <Box mt={2} mb={2} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Divider sx={{ flex: 1 }} />
            <Typography variant="body2" color="textSecondary" sx={{ px: 1, whiteSpace: 'nowrap' }}>or continue with</Typography>
            <Divider sx={{ flex: 1 }} />
          </Box>
          <Box display="flex" gap={1.5} flexDirection="column">
            {ssoProviders.map((provider) => (
              <Button key={provider.id} fullWidth variant="outlined" color="secondary"
                onClick={() => handleSSOLogin(provider.id)} disabled={ssoLoading}
                sx={{ textTransform: 'none', py: 1.2, fontWeight: 500, fontSize: '0.95rem' }}
                startIcon={
                  provider.providerType === 'google' ? (
                    <svg width="20" height="20" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.006 24.006 0 0 0 0 21.56l7.98-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
                  ) : provider.providerType === 'microsoft' ? (
                    <svg width="20" height="20" viewBox="0 0 21 21"><rect x="1" y="1" width="9" height="9" fill="#f25022"/><rect x="1" y="11" width="9" height="9" fill="#00a4ef"/><rect x="11" y="1" width="9" height="9" fill="#7fba00"/><rect x="11" y="11" width="9" height="9" fill="#ffb900"/></svg>
                  ) : (
                    <LoginIcon />
                  )
                }>
                {provider.buttonName}
              </Button>
            ))}
          </Box>
        </>
      )}

      <Box mt={2} sx={{ display: "block", textAlign: "center", mt: "1rem", color: (theme.palette.secondary as CustomPaletteColor)[200], mb: "1rem", position: "relative",
        "&:before, &:after": { content: '""', display: "block", height: "1px", width: "calc(50% - 90px)", backgroundColor: (theme.palette.secondary as CustomPaletteColor)[200], position: "absolute", top: "0.5rem" },
        "&:before": { left: "0" }, "&:after": { right: "0" },
      }}>
        <Typography variant="body2" align="center">Terms and Conditions</Typography>
      </Box>
      {loginSignInTermsHtml ? (
        <Box
          className="login-signin-terms-notice"
          sx={{
            typography: 'body2',
            textAlign: 'center',
            color: 'text.secondary',
            '& p': { mb: 0.5, '&:last-child': { mb: 0 } },
            '& a': {
              color: theme.palette.login?.anchorColor ?? 'secondary.main',
              textDecoration: 'underline',
              wordBreak: 'break-word',
            },
            '& ul, & ol': { display: 'inline-block', textAlign: 'left', pl: 2.5, my: 0.5 },
          }}
          dangerouslySetInnerHTML={{ __html: loginSignInTermsHtml }}
        />
      ) : (
        <Typography variant="body2" align="center" color="text.secondary">
          {DEFAULT_LOGIN_SIGN_IN_TERMS_NOTICE}
        </Typography>
      )}
    </Box>
  );

  const renderStep1CompanySetup = () => (
    <Box>
      <TextField margin="dense" label="Company Name *" variant="outlined" color="secondary" fullWidth size="small"
        value={signUpCompanyName} onChange={(e) => setSignUpCompanyName(e.target.value)} />
      <TextField margin="dense" label="Address Line 1 *" variant="outlined" color="secondary" fullWidth size="small"
        value={companyAddr1} onChange={(e) => setCompanyAddr1(e.target.value)} />
      <TextField margin="dense" label="Address Line 2" variant="outlined" color="secondary" fullWidth size="small"
        value={companyAddr2} onChange={(e) => setCompanyAddr2(e.target.value)} />
      <Box display="flex" gap={isNonMobile ? 2 : 1} flexDirection={isNonMobile ? 'row' : 'column'}>
        <TextField margin="dense" label="City *" variant="outlined" color="secondary" fullWidth size="small"
          value={companyCity} onChange={(e) => setCompanyCity(e.target.value)} />
        <Autocomplete<CountryOption, false, false, false>
          fullWidth
          options={signupCountryOptions}
          loading={countriesLoading}
          filterOptions={filterSignupCountryOptions}
          getOptionLabel={(option) => `${option.label} (${option.dialCode})`}
          isOptionEqualToValue={(a, b) => a.code.toUpperCase() === b.code.toUpperCase()}
          value={selectedSignupCountry}
          onChange={(_, newValue) => setCompanyCountry(newValue?.code ?? '')}
          slotProps={{
            popper: {
              sx: { zIndex: (t) => t.zIndex.modal },
            },
            paper: {
              sx: (t) => ({
                mt: 0.5,
                // Form card uses background.paper (primary blues in dark mode); use a neutral surface so the list is clearly separated
                backgroundColor: t.palette.mode === 'dark' ? t.palette.grey[900] : t.palette.grey[50],
                color: t.palette.text.primary,
                backgroundImage: 'none',
                border: `1px solid ${t.palette.divider}`,
                boxShadow: t.shadows[12],
                '& .MuiAutocomplete-listbox': { py: 0.5, maxHeight: 280 },
                '& .MuiAutocomplete-option': {
                  '&[aria-selected="true"]': {
                    backgroundColor: `${t.palette.secondary.main}33`,
                  },
                  '&.Mui-focused, &:hover': {
                    backgroundColor: t.palette.action.hover,
                  },
                },
              }),
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              margin="dense"
              label="Country *"
              variant="outlined"
              color="secondary"
              fullWidth
              size="small"
              placeholder="Type to search"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {countriesLoading ? <CircularProgress color="inherit" size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Box>
      <Box display="flex" gap={isNonMobile ? 2 : 1} flexDirection={isNonMobile ? 'row' : 'column'}>
        <TextField margin="dense" label="Postal Code *" variant="outlined" color="secondary" fullWidth size="small"
          value={companyZip} onChange={(e) => setCompanyZip(e.target.value)} />
        <TextField margin="dense" label="Company Phone" variant="outlined" color="secondary" fullWidth size="small" placeholder="+44 20 1234 5678"
          value={companyPhone} onChange={(e) => setCompanyPhone(e.target.value)} />
      </Box>
      <TextField margin="dense" label="Company Website" variant="outlined" color="secondary" fullWidth size="small" placeholder="https://example.com"
        value={companyWebsite} onChange={(e) => setCompanyWebsite(e.target.value)} />
    </Box>
  );

  const renderStep2Credentials = () => (
    <Box>
      {existingUserDetected && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          This email is already registered. A new company will be created and linked to your existing account.
          You will not need to set a new password.
        </Alert>
      )}
      {emailCheckLoading && <LinearProgress color="secondary" sx={{ mb: 1 }} />}
      <TextField margin="dense" label="Email *" variant="outlined" color="secondary" fullWidth size="small" type="email"
        value={signUpEmail} error={!!emailError} helperText={emailError}
        onChange={(e) => { setSignUpEmail(e.target.value); validateEmail(e.target.value); setExistingUserDetected(false); }}
        onBlur={() => { validateEmail(signUpEmail); checkEmailForWizard(signUpEmail); }}
        slotProps={{ htmlInput: EMAIL_FIELD_HTML_INPUT }} />
      {!existingUserDetected ? (
        <Box display="flex" gap={isNonMobile ? 2 : 1} flexDirection={isNonMobile ? 'row' : 'column'} mt={0.5}>
          <TextField margin="dense" label="Password *" variant="outlined" placeholder="Min 6 characters" color="secondary" fullWidth size="small"
            type={showPassword ? 'text' : 'password'} value={signUpPassword}
            error={!!signUpPassword && signUpPassword.length < 6}
            helperText={signUpPassword && signUpPassword.length < 6 ? 'Must be at least 6 characters' : ''}
            onChange={(e) => setSignUpPassword(e.target.value)}
            slotProps={{ input: { endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setShowPassword(!showPassword)} edge="end">
                  {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            )}}} />
          <TextField margin="dense" label="Confirm Password *" variant="outlined" placeholder="Re-enter password" color="secondary" fullWidth size="small"
            type={showConfirmPassword ? 'text' : 'password'} value={signUpConfirmPassword}
            error={!!signUpConfirmPassword && signUpPassword !== signUpConfirmPassword}
            helperText={signUpConfirmPassword && signUpPassword !== signUpConfirmPassword ? 'Passwords do not match' : ''}
            onChange={(e) => setSignUpConfirmPassword(e.target.value)}
            slotProps={{ input: { endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                  {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            )}}} />
        </Box>
      ) : (
        <Box display="flex" gap={isNonMobile ? 2 : 1} flexDirection={isNonMobile ? 'row' : 'column'} mt={0.5}>
          <TextField margin="dense" label="Password" variant="outlined" color="secondary" fullWidth size="small"
            value="••••••••" disabled />
          <TextField margin="dense" label="Confirm Password" variant="outlined" color="secondary" fullWidth size="small"
            value="••••••••" disabled />
        </Box>
      )}
    </Box>
  );

  const renderStep3PersonalDetails = () => {
    if (existingUserDetected) {
      return (
        <Box>
          <Alert severity="info" sx={{ mb: 1.5 }} icon={<CheckCircleOutlineIcon />}>
            Your existing account (<b>{signUpEmail}</b>) already has personal details on file.
            These will be used for the new company membership. You can proceed to the next step.
          </Alert>
        </Box>
      );
    }
    return (
      <Box>
        <Box display="flex" gap={isNonMobile ? 2 : 1} flexDirection={isNonMobile ? 'row' : 'column'}>
          <TextField margin="dense" label="First Name *" variant="outlined" color="secondary" fullWidth size="small"
            value={signUpFirstName} onChange={(e) => setSignUpFirstName(e.target.value)} />
          <TextField margin="dense" label="Last Name *" variant="outlined" color="secondary" fullWidth size="small"
            value={signUpLastName} onChange={(e) => setSignUpLastName(e.target.value)} />
        </Box>
        <TextField margin="dense" label="Mobile *" variant="outlined" placeholder="+44 7911123456" color="secondary" fullWidth size="small"
          value={signUpMobile} error={!!mobileError} helperText={mobileError || 'Must include country code prefix (e.g. +44, +91)'}
          onChange={(e) => { setSignUpMobile(e.target.value); validateMobile(e.target.value); }}
          onBlur={() => validateMobile(signUpMobile)} />
        <TextField margin="dense" label="Date of Birth *" variant="outlined" color="secondary" fullWidth size="small" type="date"
          value={signUpDob} error={!!dobError} helperText={dobError || 'You must be 18 or older'}
          onChange={(e) => { setSignUpDob(e.target.value); validateDob(e.target.value); }}
          onBlur={() => validateDob(signUpDob)}
          slotProps={{ inputLabel: { shrink: true } }} />
        <Box mt={1}>
          <FormControlLabel control={<Checkbox color="secondary" size="small" checked={signUpTerms} onChange={(e) => setSignUpTerms(e.target.checked)} />}
            label={<Typography variant="body2">I accept the Terms and Conditions *</Typography>} />
          <FormControlLabel control={<Checkbox color="secondary" size="small" checked={signUpPrivacy} onChange={(e) => setSignUpPrivacy(e.target.checked)} />}
            label={<Typography variant="body2">I accept the Privacy Policy *</Typography>} />
        </Box>
      </Box>
    );
  };

  const formatPrice = (amount: number, currCode: string): string => {
    const sym = getCurrencySymbol(currCode);
    const val = amount / 100;
    if (val >= 1000) return `${sym}${val.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    return `${sym}${val.toFixed(val % 1 === 0 ? 0 : 2)}`;
  };

  const renderStep4PlanSelection = () => {
    const selectedPlan = availablePlans.find(p => p.key === signUpPlan);
    return (
      <Box>
        <Typography variant="caption" color="textSecondary" display="block" textAlign="center" mb={1}>
          Currency: <b>{getCurrencySymbol(selectedCurrency)} {selectedCurrency}</b> (based on company country)
        </Typography>

        <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={1.5}>
          <Typography variant="body2" color={billingCycle === 'monthly' ? 'secondary' : 'textSecondary'} fontWeight={billingCycle === 'monthly' ? 700 : 400}>
            Monthly
          </Typography>
          <FormGroup>
            <Switch color="secondary" checked={billingCycle === 'annual'}
              onChange={(e) => setBillingCycle(e.target.checked ? 'annual' : 'monthly')} size="small" />
          </FormGroup>
          <Typography variant="body2" color={billingCycle === 'annual' ? 'secondary' : 'textSecondary'} fontWeight={billingCycle === 'annual' ? 700 : 400}>
            Annual
          </Typography>
          {billingCycle === 'annual' && (
            <Chip label="Save up to 25%" size="small" color="success" sx={{ height: 20, fontSize: '0.65rem' }} />
          )}
        </Box>

        {plansLoading ? <LinearProgress color="secondary" sx={{ mb: 1 }} /> : (
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1}>
            {availablePlans.map((plan) => {
              const isSelected = signUpPlan === plan.key;
              const price = getPlanPriceInCurrency(plan, billingCycle, selectedCurrency);
              const allFeatures = plan.highlights.flatMap(h => h.features);
              const extraModules = plan.modules.filter(m => m !== 'instantupdate.ai');
              return (
                <Card key={plan.key} variant="outlined" sx={{
                  borderColor: isSelected ? theme.palette.secondary.main : theme.palette.divider,
                  borderWidth: isSelected ? 2 : 1,
                  backgroundColor: isSelected ? `${theme.palette.secondary.main}12` : 'transparent',
                  transition: 'all 0.2s ease',
                }}>
                  <CardActionArea onClick={() => setSignUpPlan(plan.key)} sx={{ p: 1.5, height: '100%' }}>
                    <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                      <Typography variant="subtitle2" fontWeight={700} lineHeight={1.2}>{plan.label}</Typography>
                      {plan.key === 'pro' && <Chip label="Popular" size="small" color="secondary" sx={{ height: 18, fontSize: '0.65rem' }} />}
                    </Box>
                    <Typography variant="caption" color="textSecondary" display="block" mb={0.5}>{plan.tagline}</Typography>
                    {price > 0 ? (
                      <Typography variant="subtitle2" color="secondary" fontWeight={700}>
                        {formatPrice(price, selectedCurrency)}/mo
                        {billingCycle === 'annual' && <Typography component="span" variant="caption" color="textSecondary"> (12-month commitment)</Typography>}
                      </Typography>
                    ) : (
                      <Typography variant="subtitle2" color="success.main" fontWeight={700}>Free – 3 Month Trial</Typography>
                    )}
                    <Typography variant="caption" display="block" lineHeight={1.4} color="text.primary" mt={0.5}>
                      {plan.noOfUsers} {plan.noOfUsers === 1 ? 'user' : 'users'}
                    </Typography>
                    {allFeatures.map((f, i) => <Typography key={i} variant="caption" display="block" lineHeight={1.4} color="text.primary">{f}</Typography>)}
                    {extraModules.map((m) => <Typography key={m} variant="caption" display="block" lineHeight={1.4} color="text.primary">{m}</Typography>)}
                  </CardActionArea>
                </Card>
              );
            })}
          </Box>
        )}
      </Box>
    );
  };

  const renderStep5Review = () => {
    const selectedPlan = availablePlans.find(p => p.key === signUpPlan);
    const price = getSelectedPlanPrice();
    const countryLabel = countries.find(c => c.code === companyCountry)?.label || companyCountry;
    return (
      <Box>
        {existingUserDetected && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            Your existing account ({signUpEmail}) will be linked to this new company as an admin.
          </Alert>
        )}
        <Card variant="outlined" sx={{ mb: 1.5, p: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} mb={1}>Company Details</Typography>
          <Typography variant="body2"><b>{signUpCompanyName}</b></Typography>
          <Typography variant="caption" color="textSecondary">{companyAddr1}{companyAddr2 ? `, ${companyAddr2}` : ''}</Typography>
          <Typography variant="caption" display="block" color="textSecondary">{companyCity}, {countryLabel} – {companyZip}</Typography>
          {companyWebsite && <Typography variant="caption" display="block" color="textSecondary">{companyWebsite}</Typography>}
          {companyPhone && <Typography variant="caption" display="block" color="textSecondary">{companyPhone}</Typography>}
        </Card>
        <Card variant="outlined" sx={{ mb: 1.5, p: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} mb={1}>
            {existingUserDetected ? 'Account' : 'Personal Details'}
          </Typography>
          {existingUserDetected ? (
            <Typography variant="body2" color="textSecondary">
              Existing account <b>{signUpEmail}</b> will be linked as admin.
            </Typography>
          ) : (
            <>
              <Typography variant="body2">{signUpFirstName} {signUpLastName}</Typography>
              <Typography variant="caption" display="block" color="textSecondary">{signUpEmail}</Typography>
              <Typography variant="caption" display="block" color="textSecondary">{signUpMobile}</Typography>
              <Typography variant="caption" display="block" color="textSecondary">DOB: {signUpDob}</Typography>
            </>
          )}
        </Card>
        <Card variant="outlined" sx={{ mb: 1.5, p: 2, borderColor: theme.palette.secondary.main }}>
          <Typography variant="subtitle2" fontWeight={700} mb={0.5}>Selected Plan</Typography>
          <Typography variant="body2"><b>{selectedPlan?.label || signUpPlan}</b> – {billingCycle === 'annual' ? 'Annual (billed monthly)' : 'Monthly'}</Typography>
          {price > 0 ? (
            <Typography variant="subtitle2" color="secondary" fontWeight={700}>
              {formatPrice(price, selectedCurrency)}/month ({selectedCurrency})
            </Typography>
          ) : (
            <Typography variant="subtitle2" color="success.main" fontWeight={700}>Free – 3 Month Trial</Typography>
          )}
        </Card>

        <LoadingButton sx={{ mt: 0.5 }} color="secondary" loading={registering} loadingPosition="start"
          startIcon={price > 0 ? <PersonAddIcon /> : <CheckCircleOutlineIcon />}
          fullWidth variant="contained" onClick={handleCreateAccount}
          disabled={registering || registerSuccess}>
          {registering ? 'Creating Account...' : price > 0 ? 'Proceed to Payment' : 'Create Account'}
        </LoadingButton>
      </Box>
    );
  };

  const renderWizardSignUp = () => (
    <Box p="1rem" width="100%">
      {registerSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircleOutlineIcon />}>
          Registration successful! Please check your email to activate your account, then sign in.
        </Alert>
      )}
      {paymentCancelled && !registerSuccess && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setPaymentCancelled(false)}>
          Payment was cancelled. You can try again or continue with the free Starter plan.
        </Alert>
      )}
      {(formError || registerError) && !registerSuccess && (
        <Alert severity="error" sx={{ mb: 2 }}>{formError || registerError}</Alert>
      )}

      {!registerSuccess && (
        <>
          <Stepper activeStep={activeStep} alternativeLabel connector={<WizardConnector />} sx={{ mb: 2 }}>
            {WIZARD_STEPS.map((label, idx) => (
              <Step key={label} completed={idx < activeStep || (idx === 2 && existingUserDetected && activeStep > 1)}>
                <StepLabel StepIconComponent={WizardStepIcon}>
                  <Typography
                    variant="caption"
                    fontWeight={activeStep === idx ? 700 : 400}
                    color={activeStep === idx && !theme.palette.login?.stepActiveLabelColor ? 'secondary' : 'textSecondary'}
                    sx={activeStep === idx && theme.palette.login?.stepActiveLabelColor
                      ? { color: theme.palette.login.stepActiveLabelColor }
                      : undefined}
                  >
                    {label}{idx === 2 && existingUserDetected && activeStep > 1 ? ' (skipped)' : ''}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {activeStep === 0 && renderStep1CompanySetup()}
          {activeStep === 1 && renderStep2Credentials()}
          {activeStep === 2 && renderStep3PersonalDetails()}
          {activeStep === 3 && renderStep4PlanSelection()}
          {activeStep === 4 && renderStep5Review()}

          {activeStep < 4 && (
            <Box display="flex" justifyContent="space-between" mt={2}>
              <Button color="secondary" variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleBack}
                disabled={activeStep === 0} sx={{ textTransform: 'none' }}>
                Back
              </Button>
              <Button color="secondary" variant="contained" endIcon={<ArrowForwardIcon />} onClick={handleNext}
                disabled={
                  (activeStep === 0 && !isStep1Valid()) ||
                  (activeStep === 1 && !isStep2Valid()) ||
                  (activeStep === 2 && !isStep3Valid())
                }
                sx={{ textTransform: 'none' }}>
                Next
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );

  const renderJoinCompanySignUp = () => (
    <Box component="form" noValidate autoComplete="off" width="100%" justifyContent="center" onSubmit={handleJoinCompany} p="1rem">
      {companyPreviewLoading && <LinearProgress color="secondary" sx={{ mb: 1 }} />}

      {registerSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>Registration successful! Please check your email to activate your account, then sign in.</Alert>
      )}
      {paymentCancelled && !registerSuccess && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setPaymentCancelled(false)}>
          Payment was cancelled. You can try again or continue with the free Starter plan.
        </Alert>
      )}
      {(formError || registerError || companyPreviewError) && !registerSuccess && (
        <Alert severity="error" sx={{ mb: 2 }}>{formError || registerError || companyPreviewError}</Alert>
      )}

      {/* Company Details Card */}
      {companyPreview && (
        <Card variant="outlined" sx={{ mb: 1.5, borderColor: theme.palette.secondary.main }}>
          <Box p={2} display="flex" flexDirection="column" alignItems="center" textAlign="center">
            {(() => {
              const isDark = theme.palette.mode === THEME_DARK;
              const logoSrc = isDark ? companyPreview.logoDark : companyPreview.logoLight;
              const fallbackSrc = isDark ? companyPreview.logoLight : companyPreview.logoDark;
              const src = logoSrc || fallbackSrc;
              if (!src) return null;
              return (
                <Box component="img" src={`data:image/png;base64,${src}`} alt={companyPreview.name}
                  sx={{ width: '100%', maxWidth: 280, maxHeight: 80, objectFit: 'contain', mb: 1.5 }} />
              );
            })()}
            <Typography variant="subtitle1" fontWeight={700}>{companyPreview.name}</Typography>
            {companyPreview.addressLine1 && companyPreview.addressLine1 !== 'NA' && (
              <Typography variant="caption" display="block" color="textSecondary">
                {companyPreview.addressLine1}{companyPreview.city ? `, ${companyPreview.city}` : ''}{companyPreview.country ? `, ${companyPreview.country}` : ''}
              </Typography>
            )}
            {companyPreview.companyEmail && <Typography variant="caption" display="block" color="textSecondary">{companyPreview.companyEmail}</Typography>}
            {companyPreview.website && <Typography variant="caption" display="block" color="textSecondary">{companyPreview.website}</Typography>}

            {joinModules.length > 0 && companyPreview.moduleLicenseInfo && (
              <Box mt={1.5} width="100%">
                <Divider sx={{ mb: 1 }} />
                <Typography variant="caption" fontWeight={600} display="block" mb={0.5}>Module License Details</Typography>
                {joinModules.map((mod) => {
                  const info = companyPreview.moduleLicenseInfo?.find((m: ModuleLicenseInfo) => m.module === mod);
                  const hasSeats = info ? info.remainingSeats > 0 : false;
                  return (
                    <Box key={mod} display="flex" justifyContent="space-between" alignItems="center" px={1} py={0.5}>
                      <Typography variant="caption" color="text.primary">{mod}</Typography>
                      {info ? (
                        <Chip label={`${info.remainingSeats} seat${info.remainingSeats !== 1 ? 's' : ''} available`}
                          size="small" color={hasSeats ? 'success' : 'error'} variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                      ) : (
                        <Chip label="Not in plan" size="small" color="error" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </Card>
      )}

      {moduleLicenseError && <Alert severity="error" sx={{ mb: 1 }}>{moduleLicenseError}</Alert>}

      <Box component="fieldset" disabled={!!moduleLicenseError}
        sx={{ border: 'none', m: 0, p: 0, opacity: moduleLicenseError ? 0.45 : 1, pointerEvents: moduleLicenseError ? 'none' : 'auto', transition: 'opacity 0.2s ease' }}>
        <Divider sx={{ my: 1.5, borderColor: (theme.palette.secondary as CustomPaletteColor)[200] }}>
          <Typography variant="caption" color="textSecondary">Personal Details</Typography>
        </Divider>

        <Box display="flex" gap={isNonMobile ? 2 : 1} flexDirection={isNonMobile ? 'row' : 'column'}>
          <TextField margin="dense" label="First Name *" variant="outlined" color="secondary" fullWidth size="small"
            value={signUpFirstName} onChange={(e) => setSignUpFirstName(e.target.value)} />
          <TextField margin="dense" label="Last Name *" variant="outlined" color="secondary" fullWidth size="small"
            value={signUpLastName} onChange={(e) => setSignUpLastName(e.target.value)} />
        </Box>
        <TextField margin="dense" label="Email *" variant="outlined" color="secondary" fullWidth size="small" type="email"
          value={signUpEmail} error={!!emailError} helperText={emailError}
          onChange={(e) => { setSignUpEmail(e.target.value); validateEmail(e.target.value); }}
          onBlur={() => validateEmail(signUpEmail)}
          slotProps={{ htmlInput: EMAIL_FIELD_HTML_INPUT }} />
        <Box display="flex" gap={isNonMobile ? 2 : 1} flexDirection={isNonMobile ? 'row' : 'column'}>
          <TextField margin="dense" label="Password *" variant="outlined" placeholder="Min 6 characters" color="secondary" fullWidth size="small"
            type={showPassword ? 'text' : 'password'} value={signUpPassword}
            error={!!signUpPassword && signUpPassword.length < 6}
            helperText={signUpPassword && signUpPassword.length < 6 ? 'Must be at least 6 characters' : ''}
            onChange={(e) => setSignUpPassword(e.target.value)}
            slotProps={{ input: { endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setShowPassword(!showPassword)} edge="end">
                  {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            )}}} />
          <TextField margin="dense" label="Confirm Password *" variant="outlined" placeholder="Re-enter password" color="secondary" fullWidth size="small"
            type={showConfirmPassword ? 'text' : 'password'} value={signUpConfirmPassword}
            error={!!signUpConfirmPassword && signUpPassword !== signUpConfirmPassword}
            helperText={signUpConfirmPassword && signUpPassword !== signUpConfirmPassword ? 'Passwords do not match' : ''}
            onChange={(e) => setSignUpConfirmPassword(e.target.value)}
            slotProps={{ input: { endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                  {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            )}}} />
        </Box>
        <TextField margin="dense" label="Mobile *" variant="outlined" placeholder="+44 7911123456" color="secondary" fullWidth size="small"
          value={signUpMobile} error={!!mobileError} helperText={mobileError || 'Must include country code prefix (e.g. +44, +91)'}
          onChange={(e) => { setSignUpMobile(e.target.value); validateMobile(e.target.value); }}
          onBlur={() => validateMobile(signUpMobile)} />
        <TextField margin="dense" label="Date of Birth *" variant="outlined" color="secondary" fullWidth size="small" type="date"
          value={signUpDob} error={!!dobError} helperText={dobError || 'You must be 18 or older'}
          onChange={(e) => { setSignUpDob(e.target.value); validateDob(e.target.value); }}
          onBlur={() => validateDob(signUpDob)}
          slotProps={{ inputLabel: { shrink: true } }} />
        <Box mt={1}>
          <FormControlLabel control={<Checkbox color="secondary" size="small" checked={signUpTerms} onChange={(e) => setSignUpTerms(e.target.checked)} />}
            label={<Typography variant="body2">I accept the Terms and Conditions *</Typography>} />
          <FormControlLabel control={<Checkbox color="secondary" size="small" checked={signUpPrivacy} onChange={(e) => setSignUpPrivacy(e.target.checked)} />}
            label={<Typography variant="body2">I accept the Privacy Policy *</Typography>} />
        </Box>

        <LoadingButton sx={{ mt: '0.5rem' }} color="secondary" loading={registering} loadingPosition="start" startIcon={<PersonAddIcon />}
          fullWidth variant="contained" type="submit"
          disabled={registering || registerSuccess || !!moduleLicenseError || !isJoinFormValid()}>
          {registering ? 'Creating Account...' : 'Join Company'}
        </LoadingButton>
      </Box>
    </Box>
  );

  const renderSignUpForm = () => {
    if (joinCompanyId) return renderJoinCompanySignUp();
    return renderWizardSignUp();
  };

  return (
    <Box sx={{ display: 'block', padding: '0' }}>
      <Box sx={{
        display: 'flex',
        backgroundColor: theme.palette.background.default,
        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
        overflowY: 'auto', py: 2,
      }}>
        <ConnectedDotsBackground
          color={theme.palette.login?.dotsColor ?? theme.palette.secondary.main}
          speed={1}
          background={theme.palette.login?.dotsBackground
            ?? `radial-gradient(circle at 50% 30%, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 80%)`}
        />
        <Paper elevation={1} sx={{
          position: 'relative',
          zIndex: 1,
          margin: 'auto',
          padding: isNonMobile ? "1rem" : "0.5rem",
          width: isNonMobile ? 580 : '92%',
          maxWidth: 580,
          backgroundColor: theme.palette.background.paper,
          borderRadius: '20px',
          border: `1px solid ${(theme.palette.secondary as CustomPaletteColor)[200]}`,
        }}>
          <Toolbar sx={{ justifyContent: "right", minHeight: '48px !important' }}>
            <ThemeModeToggle
              preference={themePreference}
              onCycle={() => dispatch(setMode())}
            />
          </Toolbar>

          <Box color={theme.palette.secondary.main} width="100%" display="flex" justifyContent="center">
            <SiteLogo showTagLine={true} tagLine="Reach Your Audience. Reign Supreme." />
          </Box>

          {userSignUpEnabled && (
            <Box
              display="flex"
              justifyContent="center"
              mt={hasDomainThemeLogoOnLogin ? 0 : 2}
              px="1rem"
            >
              <ButtonGroup fullWidth variant="outlined" color="secondary">
                <Button variant={authMode === 'signin' ? 'contained' : 'outlined'} color="secondary" startIcon={<LoginIcon />}
                  onClick={() => switchMode('signin')} sx={{ textTransform: 'none', fontWeight: authMode === 'signin' ? 700 : 400 }}>
                  Sign In
                </Button>
                <Button variant={authMode === 'signup' ? 'contained' : 'outlined'} color="secondary" startIcon={<PersonAddIcon />}
                  onClick={() => switchMode('signup')} sx={{ textTransform: 'none', fontWeight: authMode === 'signup' ? 700 : 400 }}>
                  Sign Up
                </Button>
              </ButtonGroup>
            </Box>
          )}

          <Box sx={{ mt: userSignUpEnabled ? 0 : (hasDomainThemeLogoOnLogin ? 0 : 2) }}>
            {effectiveAuthMode === 'signin' ? renderSignInForm() : renderSignUpForm()}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
